import React from 'react';
import {
  Card,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Stack,
  Tooltip,
  Box,
} from '@mui/material';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import Typography from '@mui/material/Typography';
import { PairItem } from './ModalMappingEventDataSource';
import { CustomNoRowsOverlay } from 'components/DatatableComponent/components/GridOverlay';

interface IListColumnProps {
  items: PairItem[];
  title: string;
  subheader: string;
  onUnpair: (value: PairItem) => void;
}

const chipStyle = {
  fontSize: 10,
  padding: '0 4px',
  height: 18,
  borderRadius: 0.75,
  fontWeight: 600,
};

export function PairsList(props: IListColumnProps) {
  const { items, title, subheader, onUnpair = () => {} } = props;

  return (
    <Card
      elevation={0}
      sx={{
        width: '36%',
        border: '1px solid #E3EBF6',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        sx={{ px: 2, py: 1.5, bgcolor: '#FAFBFD', borderBottom: '1px solid #E3EBF6' }}
        avatar={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              borderRadius: '8px',
              bgcolor: items.length > 0 ? '#EEF7F3' : '#F0F3F7',
            }}
          >
            <LinkOutlinedIcon sx={{ fontSize: 16, color: items.length > 0 ? '#42BB93' : '#98A2AE' }} />
          </Box>
        }
        title={
          <Stack>
            <Typography variant='input-label' fontSize={13} fontWeight={800}>
              {title}
            </Typography>
            <Typography variant='input-label-gray' fontSize={12}>
              {subheader}
            </Typography>
          </Stack>
        }
      />
      <List
        sx={{
          height: 330,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        dense
        component='div'
        role='list'
      >
        {items.length === 0 && <CustomNoRowsOverlay label='No columns paired yet' />}

        {items.map((pairItem, index) => {
          const labelId = `${pairItem.sourceColumn.columnName}-${pairItem.targetColumn.columnName}-label`;

          return (
            <React.Fragment key={`${pairItem.sourceColumn.columnName}-${pairItem.targetColumn.columnName}`}>
              <ListItem
                role='listitem'
                sx={{
                  py: 1,
                  px: 2,
                  bgcolor: index % 2 === 0 ? '#FAFBFD' : '#fff',
                  '&:hover': { bgcolor: '#FEF2F2' },
                  '&:hover .unpair-btn': { opacity: 1 },
                  transition: 'background-color 0.15s ease',
                }}
              >
                <ListItemText
                  id={labelId}
                  primary={
                    <Stack direction='row' gap={0.75} alignItems='center' flexWrap='wrap'>
                      <Typography variant='body2' fontSize={13} fontWeight={600} sx={{ color: '#141414' }}>
                        {pairItem.sourceColumn.columnName}
                      </Typography>
                      <EastOutlinedIcon sx={{ color: '#42BB93', fontSize: 14 }} />
                      <Typography variant='body2' fontSize={13} fontWeight={600} sx={{ color: '#3B4797' }}>
                        {pairItem.targetColumn.columnName}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Chip
                      label={pairItem.targetColumn.dataTypeLabel || pairItem.targetColumn.dataType}
                      color='info'
                      variant='outlined'
                      sx={chipStyle}
                      size='small'
                    />
                  }
                />
                <Tooltip title='Remove pairing' placement='left'>
                  <IconButton
                    className='unpair-btn'
                    size='small'
                    onClick={() => onUnpair(pairItem)}
                    sx={{
                      opacity: 0.5,
                      color: '#BB4942',
                      transition: 'opacity 0.15s ease',
                      '&:hover': { bgcolor: '#FEE2E2' },
                    }}
                  >
                    <LinkOffOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </ListItem>
              <Divider sx={{ borderColor: '#F0F3F7' }} />
            </React.Fragment>
          );
        })}
      </List>
    </Card>
  );
}
